import {
    h, Component, toChildArray, ComponentChildren, VNode, Fragment, isValidElement, createRef
} from "preact";

import { Keyboard } from "../../../foundation/enums";
import classname from "../../../foundation/utils/classname";
import { stopPropagation } from "../../../foundation/utils/legacy";
import { objectEqual } from "../../../foundation/utils/object";
import { scrollIfNecessary } from "../../../foundation/utils/platform";

import MenuContext, { HighlightableMenuItems } from "./MenuContext";
import { toMenuItemChildren } from "./withMenuItem";

import "./style.css";

interface MenuProps {
    class?: string;
    // TODO-electron-tech-debt-onur: Remove this prop when the max-height issue is fixed
    /* This is a temporary prop to manually apply fixed max-height */
    applyMaxHeight?: boolean;
    overlayPositioned?: boolean;
    portalInto?: string | HTMLElement;
    title?: string;
    description?: string;
    children: ComponentChildren;
    footer?: ComponentChildren;
    shouldCloseOnItemClick?: boolean;
    shouldStopPropagateOnOutsideClick?: boolean;
    shouldCloseOnOutsideClick?: boolean;
    autoScrollOnVerticalNavigation?: boolean;
    ignoreMouseEventsOnKeyboardNavigation?: boolean;
    ignoredKeyDownEventKeys?: {
        arrowUp?: boolean;
        arrowDown?: boolean;
        arrowRight?: boolean;
        arrowLeft?: boolean;
        enter?: boolean;
        space?: boolean;
        escape?: boolean;
        tab?: boolean;
    };
    shouldHighlightFirstItem?: boolean;
    onItemClick?: (event: Event, value?: string) => void;
    onOpen?: () => void;
    onClose: () => void;
}

class Menu extends Component<MenuProps> {
    override state: MenuContext = {
        highlightables: {},
        highlightedIndicesPath: [],
        lastHighlightableOpen: false,
        keyboardNavigationIsActive: false
    };

    baseRef = createRef<HTMLDivElement>();

    resetHighlight() {
        const { shouldHighlightFirstItem } = this.props;
        const { highlightables } = this.state;
        const indicesOfFirstLevelHighlightables = Object.keys(highlightables);

        if (indicesOfFirstLevelHighlightables.length) {
            this.setState({
                highlightedIndicesPath: shouldHighlightFirstItem ? [indicesOfFirstLevelHighlightables[0]] : [],
                lastHighlightableOpen: false
            });
        }
    }

    anyNonIgnoredArrowKeyPressed = (ev: KeyboardEvent) : boolean => {
        const { ignoredKeyDownEventKeys } = this.props;

        return (
            (ev.key === Keyboard.Key.ArrowUp && !ignoredKeyDownEventKeys?.arrowUp) ||
            (ev.key === Keyboard.Key.ArrowDown && !ignoredKeyDownEventKeys?.arrowDown) ||
            (ev.key === Keyboard.Key.ArrowLeft && !ignoredKeyDownEventKeys?.arrowLeft) ||
            (ev.key === Keyboard.Key.ArrowRight && !ignoredKeyDownEventKeys?.arrowRight)
        );
    };

    handleMenuChildClick = (event: Event, value?: string) => {
        this.props.onItemClick?.(event, value);

        if (this.props.shouldCloseOnItemClick) {
            this.props.onClose();
        }
    };

    handleMenuChildMouseEnter = (indicesPath: string[]) => {
        if (this.props.ignoreMouseEventsOnKeyboardNavigation && this.state.keyboardNavigationIsActive) {
            return;
        }

        this.setState({
            highlightedIndicesPath: indicesPath,
            lastHighlightableOpen: true
        });
    };

    handleMenuChildMouseMove = (indicesPath: string[]) => {
        if (this.props.ignoreMouseEventsOnKeyboardNavigation && this.state.keyboardNavigationIsActive) {
            return;
        }

        this.setState({
            highlightedIndicesPath: indicesPath,
            lastHighlightableOpen: true
        });
    };

    handleMenuChildMouseLeave = () => {
        if (this.props.ignoreMouseEventsOnKeyboardNavigation && this.state.keyboardNavigationIsActive) {
            return;
        }

        this.setState({
            highlightedIndicesPath: [],
            lastHighlightableOpen: false
        });
    };

    handleMouseMove = () => {
        if (this.props.ignoreMouseEventsOnKeyboardNavigation && this.state.keyboardNavigationIsActive) {
            this.setState({
                keyboardNavigationIsActive: false
            });
        }
    };

    handleDocumentClick = (ev: MouseEvent) => {
        if (!this.baseRef.current!.contains(ev.target as HTMLElement)) {
            if (this.props.shouldStopPropagateOnOutsideClick) {
                ev.stopPropagation();
            }

            if (this.props.shouldCloseOnOutsideClick) {
                this.props.onClose();
            }
        }
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        const { ignoredKeyDownEventKeys: ignoredKeys = {} } = this.props;

        if (this.anyNonIgnoredArrowKeyPressed(ev) && this.props.ignoreMouseEventsOnKeyboardNavigation) {
            this.setState({
                keyboardNavigationIsActive: true
            });
        }

        if (ev.key === Keyboard.Key.ArrowUp && !ignoredKeys.arrowUp) {
            this.handleVerticalNavigation(-1);
            ev.stopPropagation();
            ev.preventDefault();
        } else if (ev.key === Keyboard.Key.ArrowDown && !ignoredKeys.arrowDown) {
            this.handleVerticalNavigation(1);
            ev.stopPropagation();
            ev.preventDefault();
        } else if (ev.key === Keyboard.Key.ArrowRight && !ignoredKeys.arrowRight) {
            this.handleRightNavigation(false);
            ev.stopPropagation();
            ev.preventDefault();
        } else if (ev.key === Keyboard.Key.ArrowLeft && !ignoredKeys.arrowLeft) {
            this.handleHorizontalBackNavigation();
            ev.stopPropagation();
            ev.preventDefault();
        } else if (
            (ev.key === Keyboard.Key.Enter && !ignoredKeys.enter) ||
            (ev.code === Keyboard.Code.Space && !ignoredKeys.space)
        ) {
            this.handleSelection();
            ev.stopPropagation();
            ev.preventDefault();
        } else if (ev.key === Keyboard.Key.Escape && !ignoredKeys.escape) {
            this.props.onClose();
            ev.stopPropagation();
            ev.preventDefault();
        } else if (ev.key === Keyboard.Key.Tab && !ignoredKeys.tab) {
            this.props.onClose();
            ev.stopPropagation();
        }
    };

    handleSelection() {
        if (this.handleRightNavigation(true)) {
            return;
        }

        const { highlightables, highlightedIndicesPath } = this.state;
        const remainingIndicesPath = [...highlightedIndicesPath];

        let activeHighlightables: HighlightableMenuItems | null = highlightables;
        while (remainingIndicesPath.length - 1) {
            activeHighlightables = activeHighlightables![remainingIndicesPath[0]].submenu;
            remainingIndicesPath.shift();
        }

        const activeHighlightable = activeHighlightables![remainingIndicesPath[0]];
        activeHighlightable?.ref.current!.click();
    }

    handleRightNavigation = (openOnly: boolean) => {
        const { highlightables, highlightedIndicesPath, lastSubmenuPosition } = this.state;
        const remainingIndicesPath = [...highlightedIndicesPath];

        let activeHighlightables: HighlightableMenuItems | null = highlightables;
        while (remainingIndicesPath.length) {
            activeHighlightables = activeHighlightables![remainingIndicesPath[0]].submenu;
            remainingIndicesPath.shift();
        }

        if (!activeHighlightables) {
            if (!openOnly && lastSubmenuPosition === "left") {
                return this.handleHorizontalBackNavigation();
            }

            return false;
        }

        const indicesOfActiveLevelHighlightables = Object.keys(activeHighlightables);
        if (!indicesOfActiveLevelHighlightables.length) {
            this.setState({
                lastHighlightableOpen: true
            });
            return true;
        }

        const newHighlightedIndicesPath = highlightedIndicesPath.concat(indicesOfActiveLevelHighlightables[0]);
        this.setState({
            highlightedIndicesPath: newHighlightedIndicesPath,
            lastHighlightableOpen: false
        });
        return true;
    };

    handleHorizontalBackNavigation = () => {
        const { highlightedIndicesPath, lastHighlightableOpen } = this.state;
        if (highlightedIndicesPath.length <= 1) {
            if (!lastHighlightableOpen) {
                return false;
            }

            this.setState({
                lastHighlightableOpen: false
            });
            return true;
        }

        const newHighlightedIndicesPath = highlightedIndicesPath.slice(0, -1);
        this.setState({
            highlightedIndicesPath: newHighlightedIndicesPath,
            lastHighlightableOpen: false
        });
        return true;
    };

    handleVerticalNavigation = (step: number) => {
        const { highlightables } = this.state;
        const highlightedIndicesPath = [...this.state.highlightedIndicesPath];
        let activeHighlightables = highlightables;
        while (highlightedIndicesPath.length > 1) {
            activeHighlightables = highlightables[highlightedIndicesPath[0]].submenu!;
            highlightedIndicesPath.shift();
        }

        if (!activeHighlightables) {
            return;
        }

        const [highlightedIndex] = highlightedIndicesPath;
        const indicesOfActiveHighlightables = Object.keys(activeHighlightables);
        const indexOfHighlightedInActiveHighlightables = indicesOfActiveHighlightables.indexOf(highlightedIndex);
        const nextIndexOfHighlightedInActiveHighlightables = indexOfHighlightedInActiveHighlightables + step;
        if (nextIndexOfHighlightedInActiveHighlightables >= 0 &&
            nextIndexOfHighlightedInActiveHighlightables < indicesOfActiveHighlightables.length) {
            const newHighlightedActiveIndex =
                indicesOfActiveHighlightables[nextIndexOfHighlightedInActiveHighlightables];
            const newHighlightedIndicesPath =
                this.state.highlightedIndicesPath.slice(0, -1).concat(newHighlightedActiveIndex);

            if (newHighlightedIndicesPath.length === 1 && this.props.autoScrollOnVerticalNavigation) {
                const newHighlight = this.state.highlightables[newHighlightedActiveIndex];
                if (newHighlight.ref.current) {
                    scrollIfNecessary(newHighlight.ref.current);
                }
            }

            this.setState({
                highlightedIndicesPath: newHighlightedIndicesPath,
                lastHighlightableOpen: false
            });
        }
    };

    override componentDidMount() {
        document.addEventListener("click", this.handleDocumentClick, true);

        document.addEventListener("keydown", this.handleKeyDown, true);

        this.resetHighlight();
        this.props.onOpen?.();
    }

    override componentDidUpdate(prevProps: MenuProps, prevState: MenuContext) {
        if (prevProps.children !== this.props.children) {
            this.setState({
                highlightables: {}
            });
        }

        const highlightablesChanged = !objectEqual(
            prevState.highlightables,
            this.state.highlightables,
            ({ ref: prevRef }, { ref }) => prevRef.current === ref.current
        );

        if (highlightablesChanged) {
            this.resetHighlight();
        }
    }

    override componentWillUnmount() {
        document.removeEventListener("click", this.handleDocumentClick, true);
        document.removeEventListener("keydown", this.handleKeyDown, true);
    }

    renderHeader() {
        const {
            title,
            description
        } = this.props;

        if (!title && !description) {
            return null;
        }

        return (
            <div class="header">
                {title && (
                    <h1 class={classname("title", "body-1")}>
                        {title}
                    </h1>
                )}
                {description && (
                    <p class={classname("description", "caption-3")}>
                        {description}
                    </p>
                )}
            </div>
        );
    }

    render() {
        const {
            class: cssClass,
            children: rawChildren,
            footer,
            applyMaxHeight,
            overlayPositioned
        } = this.props;
        const { highlightedIndicesPath, lastHighlightableOpen } = this.state;

        const classnames = classname("zplMenu", cssClass, { overlayPositioned });

        const children = toChildArray(rawChildren).flatMap(
            child => (isValidElement(child) && child.type === Fragment ? toChildArray(child.props.children) : child)
        ) as VNode[];

        return (
            <MenuContext.Provider value={this.state}>
                <div
                    ref={this.baseRef}
                    class={classnames}
                    onClick={stopPropagation}
                    onMouseMove={this.handleMouseMove}>
                    <div class={classname({ applyMaxHeight })}>
                        {this.renderHeader()}
                        {toMenuItemChildren(children, {
                            highlightedIndicesPath,
                            lastHighlightableOpen,
                            handleMenuChildClick: this.handleMenuChildClick,
                            handleMenuChildMouseEnter: this.handleMenuChildMouseEnter,
                            handleMenuChildMouseLeave: this.handleMenuChildMouseLeave,
                            handleMenuChildMouseMove:
                                this.props.ignoreMouseEventsOnKeyboardNavigation
                                    ? this.handleMenuChildMouseMove
                                    : undefined
                        })}
                    </div>
                    {footer}
                </div>
            </MenuContext.Provider>
        );
    }
}

Menu.defaultProps = {
    shouldCloseOnItemClick: true,
    shouldStopPropagateOnOutsideClick: true,
    shouldCloseOnOutsideClick: true,
    autoScrollOnVerticalNavigation: false,
    ignoreMouseEventsOnKeyboardNavigation: false,
    ignoredKeyDownEventKeys: {},
    shouldHighlightFirstItem: true
};

export default Menu;
export { MenuProps };
